<div data-spartan-page class="p-6 md:p-8 space-y-5"
     style="background: rgb(var(--ds-bg, 250 250 249)); min-height: 100%;">

  <!-- Page header -->
  <app-page-header
    eyebrow="Shopee"
    title="Shopee Orders"
    subtitle="Live order pipeline. Real-time updates via SignalR."
  >
    <div actions class="flex items-center gap-2">
      @if (signalRConnected) {
        <span class="inline-flex items-center gap-1.5 text-xs text-green-700 font-medium">
          <span class="relative flex w-2 h-2">
            <span class="absolute inline-flex w-full h-full rounded-full bg-green-400 opacity-75 animate-ping"></span>
            <span class="relative inline-flex w-2 h-2 rounded-full bg-green-500"></span>
          </span>
          Live
        </span>
      } @else {
        <span class="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <span class="w-2 h-2 rounded-full bg-stone-300"></span>
          Disconnected
        </span>
      }
      <button hlmBtn variant="outline" size="sm"
              (click)="toggleScanningActivity()">
        <ng-icon hlm name="lucideActivity" class="mr-1.5 size-3.5" />
        {{ showScanningActivity ? 'Hide' : 'Show' }} activity
      </button>
    </div>
  </app-page-header>

  <!-- Error alert -->
  @if (errorMessage) {
    <app-inline-alert tone="danger" [message]="errorMessage" />
  }

  <!-- Main content grid: table + optional activity feed -->
  <div class="grid gap-5 transition-[grid-template-columns]"
       [class]="showScanningActivity && realtimeScanActivity.length > 0
                  ? 'grid-cols-1 xl:grid-cols-[1fr_320px]'
                  : 'grid-cols-1'">

    <!-- ── Orders card ── -->
    <div hlmCard class="min-w-0 overflow-hidden">

      <!-- Toolbar -->
      <div class="px-4 py-3 border-b border-border flex flex-wrap items-center gap-2">

        <!-- Search -->
        <div class="flex-1 min-w-[220px] max-w-sm relative">
          <ng-icon hlm name="lucideSearch"
                   class="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 pointer-events-none text-muted-foreground" />
          <input
            hlmInput
            type="text"
            class="pl-8 h-9 w-full"
            placeholder="Search by order #, Shopee #, tracking…"
            [(ngModel)]="filterParams.searchTerm"
            (keyup.enter)="applyFilters()"
          />
        </div>

        <!-- Status filter — canonical dropdown -->
        <div class="relative">
          <select class="appearance-none h-9 border border-border-strong bg-surface rounded-md pl-3 pr-8 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent w-44"
                  [(ngModel)]="filterParams.status"
                  (change)="applyFilters()">
            <option [ngValue]="undefined">All statuses</option>
            @for (s of availableStatuses; track s) {
              <option [value]="s">{{ s }}</option>
            }
          </select>
          <ng-icon hlm name="lucideChevronDown"
                   class="absolute right-2 top-1/2 -translate-y-1/2 size-4 pointer-events-none text-muted-foreground" />
        </div>

        <!-- Date range -->
        <input hlmInput type="date" class="h-9 text-sm w-36"
               [(ngModel)]="filterParams.dateFrom"
               title="Date from" />
        <input hlmInput type="date" class="h-9 text-sm w-36"
               [(ngModel)]="filterParams.dateTo"
               title="Date to" />

        <button hlmBtn variant="outline" size="sm" (click)="applyFilters()">
          <ng-icon hlm name="lucideSearch" class="mr-1.5 size-3.5" />
          Apply
        </button>
        <button hlmBtn variant="ghost" size="sm" (click)="clearFilters()">
          Clear
        </button>

      </div>

      <!-- Loading skeleton -->
      @if (isLoading) {
        <div class="p-4">
          <app-skeleton [rows]="5" />
        </div>

      <!-- Empty state -->
      } @else if (ordersResponse && ordersResponse.items.length === 0) {
        <app-empty-state
          icon="lucideInbox"
          heading="No orders found"
          description="Adjust your filters or wait for incoming orders."
        />

      <!-- Table -->
      } @else if (ordersResponse && ordersResponse.items.length > 0) {
        <div class="overflow-x-auto">
          <table class="w-full text-sm" aria-label="Shopee orders">
            <thead>
              <tr class="text-[11px] font-medium uppercase tracking-wider border-b border-border-strong text-muted-foreground">
                <th class="w-9"></th><!-- expand toggle -->
                <th class="text-left px-4 py-2.5">Order #</th>
                <th class="text-left px-4 py-2.5">Shopee Order #</th>
                <th class="text-left px-4 py-2.5">Carrier TN</th>
                <th class="text-left px-4 py-2.5">Carton #</th>
                <th class="text-left px-4 py-2.5">Status</th>
                <th class="text-right px-4 py-2.5">Parcels</th>
                <th class="text-left px-4 py-2.5">Created</th>
                <th class="text-left px-4 py-2.5">Updated</th>
                <th class="text-left px-4 py-2.5">Destination</th>
                <th class="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              @for (order of ordersResponse.items; track (order.uniqueId || $index); let i = $index) {
                <!-- Main row -->
                <tr class="border-b border-border last:border-0 transition-colors even:bg-surface-raised/50 hover:bg-surface-raised group"
                    [class.bg-accent-light]="isOrderHighlighted(order.uniqueId)"
                    [class.highlighted-order]="isOrderHighlighted(order.uniqueId)"
                    [title]="isOrderHighlighted(order.uniqueId) ? 'New order received' : ''">

                  <!-- Expand toggle — reset UA button box -->
                  <td class="px-3">
                    <button
                      class="appearance-none border-0 bg-transparent grid place-items-center w-6 h-6 rounded transition text-muted-foreground hover:bg-surface-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                      [class.text-accent]="isOrderExpanded(i)"
                      (click)="toggleOrderExpansion(i)"
                      [disabled]="order.parcelCount === 0"
                      [attr.aria-label]="isOrderExpanded(i) ? 'Collapse' : 'Expand parcels'"
                      [title]="order.parcelCount > 0 ? 'Show/hide parcels' : 'No parcels to show'">
                      @if (order.parcelCount > 0) {
                        <ng-icon hlm name="lucideChevronRight"
                                 class="size-3.5 transition-transform"
                                 [class.rotate-90]="isOrderExpanded(i)" />
                      } @else {
                        <ng-icon hlm name="lucideMinus" class="size-3.5" />
                      }
                    </button>
                  </td>

                  <td class="px-4 h-12">
                    <div class="flex items-center gap-2">
                      <span class="font-mono text-[12.5px] font-medium">{{ order.uniqueId }}</span>
                      @if (isOrderBeingScanned(order.uniqueId)) {
                        <span class="inline-flex items-center gap-1 text-[10px] text-accent font-medium">
                          <span class="w-1.5 h-1.5 rounded-full bg-accent animate-pulse"></span>
                          scanning
                        </span>
                      }
                    </div>
                  </td>
                  <td class="px-4 h-12 font-mono text-[12px]">{{ order.shopeeOrderNo }}</td>
                  <td class="px-4 h-12 font-mono text-[12px] text-muted-foreground tabular-nums">
                    {{ order.carrierTrackingNumber || '—' }}
                  </td>
                  <td class="px-4 h-12 text-sm text-muted-foreground">
                    {{ order.cartonNo || '—' }}
                  </td>
                  <td class="px-4 h-12">
                    <app-status-badge
                      [variant]="getOrderStatusVariant(order.status)"
                      [label]="order.status"
                    />
                  </td>
                  <td class="px-4 h-12 text-right tabular-nums">
                    <span class="text-sm font-medium">{{ order.parcelCount }}</span>
                  </td>
                  <td class="px-4 h-12 text-xs tabular-nums text-muted-foreground">
                    {{ order.createdAt | date:'yyyy-MM-dd HH:mm' }}
                  </td>
                  <td class="px-4 h-12 text-xs tabular-nums text-muted-foreground">
                    {{ order.lastUpdated | date:'yyyy-MM-dd HH:mm' }}
                  </td>
                  <td class="px-4 h-12 text-sm">{{ order.destinationRegion || '—' }}</td>
                  <td class="px-4 h-12 text-right">
                    <div class="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition">
                      <button hlmBtn variant="ghost" size="sm"
                              class="h-7 w-7 p-0"
                              (click)="viewOrderDetails(order.uniqueId)"
                              title="View details">
                        <ng-icon hlm name="lucideEye" class="size-3.5" />
                      </button>
                      @if (order.parcelCount > 0) {
                        <button hlmBtn variant="ghost" size="sm"
                                class="h-7 w-7 p-0"
                                (click)="exportOrderParcels(order.uniqueId, order.shopeeOrderNo)"
                                [disabled]="isExporting && exportingOrderId === order.uniqueId"
                                title="Export CSV">
                          @if (isExporting && exportingOrderId === order.uniqueId) {
                            <ng-icon hlm name="lucideLoader" class="size-3.5 animate-spin" />
                          } @else {
                            <ng-icon hlm name="lucideDownload" class="size-3.5" />
                          }
                        </button>
                      }
                    </div>
                  </td>
                </tr>

                <!-- Expandable parcels panel -->
                @if (isOrderExpanded(i) && order.parcelCount > 0) {
                  <tr class="border-b border-border last:border-0 bg-surface-raised/80">
                    <td></td>
                    <td colspan="10" class="px-0 py-0">
                      <div class="border-l-2 border-accent ml-4 my-2 pl-4 pr-4 py-3">

                        <!-- Panel header -->
                        <div class="flex items-center justify-between mb-2.5">
                          <div class="text-xs font-medium text-muted-foreground">
                            {{ order.parcelCount }} parcels in {{ order.shopeeOrderNo }}
                          </div>
                          <div class="flex items-center gap-2">
                            @if (!orderParcelsCache[order.uniqueId]) {
                              <button hlmBtn variant="outline" size="sm"
                                      class="h-7 text-xs"
                                      (click)="loadOrderParcels(order.uniqueId, i)"
                                      [disabled]="isLoadingParcels">
                                @if (isLoadingParcels && loadingParcelOrderIndex === i) {
                                  <ng-icon hlm name="lucideLoader" class="mr-1.5 size-3.5 animate-spin" />
                                } @else {
                                  <ng-icon hlm name="lucideDownload" class="mr-1.5 size-3.5" />
                                }
                                Load Parcels
                              </button>
                            } @else {
                              <button hlmBtn variant="outline" size="sm"
                                      class="h-7 text-xs"
                                      (click)="exportOrderParcels(order.uniqueId, order.shopeeOrderNo)"
                                      [disabled]="isExporting">
                                <ng-icon hlm name="lucideDownload" class="mr-1.5 size-3.5" />
                                Export CSV
                              </button>
                            }
                          </div>
                        </div>

                        <!-- Parcel search (for large sets) -->
                        @if (order.parcelCount > 10 && orderParcelsCache[order.uniqueId]) {
                          <div class="flex items-center gap-2 mb-2">
                            <div class="relative w-48">
                              <ng-icon hlm name="lucideSearch"
                                       class="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 pointer-events-none text-muted-foreground" />
                              <input hlmInput
                                     type="text"
                                     class="pl-7 h-7 text-xs w-full"
                                     placeholder="Filter parcels…"
                                     [(ngModel)]="parcelSearchTerms[order.uniqueId]"
                                     (input)="onParcelSearch(order.uniqueId)" />
                            </div>
                            <!-- Parcel page-size picker — canonical dropdown -->
                            <div class="relative">
                              <select class="appearance-none h-7 border border-border-strong bg-surface rounded-md pl-2 pr-7 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                                      [(ngModel)]="parcelPageSizes[order.uniqueId]"
                                      (change)="onParcelPageSizeChange(order.uniqueId)">
                                <option [value]="10">10/page</option>
                                <option [value]="25">25/page</option>
                                <option [value]="50">50/page</option>
                                <option [value]="100">100/page</option>
                                @if (order.parcelCount <= 500) {
                                  <option [value]="-1">Show all</option>
                                }
                              </select>
                              <ng-icon hlm name="lucideChevronDown"
                                       class="absolute right-1.5 top-1/2 -translate-y-1/2 size-3 pointer-events-none text-muted-foreground" />
                            </div>
                            <span class="text-[11px] text-muted-foreground">
                              {{ getFilteredParcels(order.uniqueId).length }} / {{ order.parcelCount }}
                            </span>
                          </div>
                        }

                        <!-- Parcels table -->
                        @if (orderParcelsCache[order.uniqueId]) {
                          <div class="overflow-y-auto" style="max-height: 400px">
                            <table class="w-full text-[13px]">
                              <thead class="sticky top-0 bg-surface-raised">
                                <tr class="text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground border-b border-border-strong">
                                  <th class="text-left px-3 py-1.5">Tracking #</th>
                                  <th class="text-right px-3 py-1.5">Weight</th>
                                  <th class="text-left px-3 py-1.5">Dimensions</th>
                                  <th class="text-left px-3 py-1.5">Receiver Name</th>
                                  <th class="text-left px-3 py-1.5">Receiver Address</th>
                                </tr>
                              </thead>
                              <tbody>
                                @for (parcel of getPaginatedParcels(order.uniqueId);
                                      track parcel.trackingNumber ?? $index) {
                                  <tr class="border-t border-border even:bg-surface-raised/50 hover:bg-surface-raised">
                                    <td class="px-3 py-1.5 font-mono text-[12px]">
                                      {{ parcel.trackingNumber }}
                                    </td>
                                    <td class="px-3 py-1.5 text-right tabular-nums">
                                      @if (parcel.weight) {
                                        {{ parcel.weight }} kg
                                      } @else {
                                        <span class="text-muted-foreground">—</span>
                                      }
                                    </td>
                                    <td class="px-3 py-1.5 font-mono text-[12px] text-muted-foreground">
                                      @if (parcel.length && parcel.width && parcel.height) {
                                        {{ parcel.length }}×{{ parcel.width }}×{{ parcel.height }} cm
                                      } @else {
                                        —
                                      }
                                    </td>
                                    <td class="px-3 py-1.5 text-[12px]">
                                      {{ parcel.Receiver_info?.receiver_name || '—' }}
                                    </td>
                                    <td class="px-3 py-1.5 text-[12px] text-muted-foreground">
                                      @if (parcel.Receiver_info?.receiver_address) {
                                        <span class="line-clamp-1 max-w-[200px] block"
                                              [title]="parcel.Receiver_info!.receiver_address!">
                                          {{ parcel.Receiver_info?.receiver_address }}{{ parcel.Receiver_info?.receiver_city ? ', ' + parcel.Receiver_info?.receiver_city : '' }}
                                        </span>
                                      } @else {
                                        —
                                      }
                                    </td>
                                  </tr>
                                }
                                @if (getFilteredParcels(order.uniqueId).length === 0) {
                                  <tr>
                                    <td colspan="5" class="text-center py-4 text-sm text-muted-foreground">
                                      No parcels match your search.
                                    </td>
                                  </tr>
                                }
                              </tbody>
                            </table>
                          </div>

                          <!-- Parcel pagination -->
                          @if (getTotalParcelPages(order.uniqueId) > 1) {
                            <div class="flex items-center justify-between mt-2 pt-2 border-t border-border">
                              <span class="text-[11px] text-muted-foreground">
                                Page {{ getCurrentParcelPage(order.uniqueId) }}
                                of {{ getTotalParcelPages(order.uniqueId) }}
                              </span>
                              <div class="flex items-center gap-1">
                                <button hlmBtn variant="ghost" size="sm"
                                        class="h-7 px-2"
                                        [disabled]="getCurrentParcelPage(order.uniqueId) === 1"
                                        (click)="goToParcelPage(order.uniqueId, getCurrentParcelPage(order.uniqueId) - 1)">
                                  <ng-icon hlm name="lucideChevronLeft" class="size-3.5" />
                                </button>
                                @for (pg of getParcelPageNumbers(order.uniqueId); track pg) {
                                  <button hlmBtn
                                          [variant]="pg === getCurrentParcelPage(order.uniqueId) ? 'default' : 'ghost'"
                                          size="sm"
                                          class="h-7 w-7 p-0 text-xs"
                                          (click)="goToParcelPage(order.uniqueId, pg)">
                                    {{ pg }}
                                  </button>
                                }
                                <button hlmBtn variant="ghost" size="sm"
                                        class="h-7 px-2"
                                        [disabled]="getCurrentParcelPage(order.uniqueId) === getTotalParcelPages(order.uniqueId)"
                                        (click)="goToParcelPage(order.uniqueId, getCurrentParcelPage(order.uniqueId) + 1)">
                                  <ng-icon hlm name="lucideChevronRight" class="size-3.5" />
                                </button>
                              </div>
                            </div>
                          }

                        } @else if (isLoadingParcels && loadingParcelOrderIndex === i) {
                          <div class="flex items-center gap-2 py-3 text-sm text-muted-foreground">
                            <ng-icon hlm name="lucideLoader" class="size-4 animate-spin" />
                            Loading parcels…
                          </div>
                        }

                        <!-- Parcel load error -->
                        @if (parcelLoadErrors[order.uniqueId]) {
                          <app-inline-alert tone="danger"
                                           [message]="parcelLoadErrors[order.uniqueId]">
                          </app-inline-alert>
                          <button hlmBtn variant="outline" size="sm"
                                  class="mt-2 h-7 text-xs"
                                  (click)="loadOrderParcels(order.uniqueId, i)">
                            <ng-icon hlm name="lucideRefreshCw" class="mr-1.5 size-3.5" />
                            Retry
                          </button>
                        }

                      </div>
                    </td>
                  </tr>
                }
              }
            </tbody>
          </table>
        </div>

        <!-- Pagination footer -->
        @if (ordersResponse && ordersResponse.totalPages > 0) {
          <div class="px-4 py-3 border-t border-border flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
            <div class="flex items-center gap-2">
              <span>Items per page:</span>
              <!-- Page-size picker — canonical dropdown -->
              <div class="relative">
                <select class="appearance-none h-7 border border-border-strong bg-surface rounded-md pl-2 pr-7 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent w-20"
                        [ngModel]="filterParams.pageSize"
                        (change)="onPageSizeChange($event)">
                  <option [value]="10">10</option>
                  <option [value]="20">20</option>
                  <option [value]="50">50</option>
                  <option [value]="100">100</option>
                  <option [value]="200">200</option>
                </select>
                <ng-icon hlm name="lucideChevronDown"
                         class="absolute right-1.5 top-1/2 -translate-y-1/2 size-3 pointer-events-none text-muted-foreground" />
              </div>
              <span>
                Page {{ ordersResponse.pageNumber }} of {{ ordersResponse.totalPages }}
                ({{ ordersResponse.totalCount }} total)
              </span>
            </div>
            <div class="flex items-center gap-1">
              <button hlmBtn variant="ghost" size="sm"
                      class="h-7 px-2"
                      [disabled]="ordersResponse.pageNumber === 1"
                      (click)="onPageChange(ordersResponse.pageNumber - 1)">
                <ng-icon hlm name="lucideChevronLeft" class="size-4" />
              </button>
              @for (pg of getPagesArray(); track pg) {
                <button hlmBtn
                        [variant]="pg === ordersResponse.pageNumber ? 'default' : 'ghost'"
                        size="sm"
                        class="h-7 w-7 p-0 text-xs"
                        (click)="onPageChange(pg)">
                  {{ pg }}
                </button>
              }
              <button hlmBtn variant="ghost" size="sm"
                      class="h-7 px-2"
                      [disabled]="ordersResponse.pageNumber === ordersResponse.totalPages"
                      (click)="onPageChange(ordersResponse.pageNumber + 1)">
                <ng-icon hlm name="lucideChevronRight" class="size-4" />
              </button>
            </div>
          </div>
        }
      }
    </div><!-- /orders card -->

    <!-- ── Live activity feed ── -->
    @if (showScanningActivity && realtimeScanActivity.length > 0) {
      <div hlmCard class="self-start"
           style="position: sticky; top: 72px">
        <div class="px-4 py-3 border-b border-border flex items-center justify-between">
          <div>
            <div class="text-sm font-semibold flex items-center gap-2">
              <span class="relative flex w-2 h-2">
                <span class="absolute inline-flex w-full h-full rounded-full bg-green-400 opacity-75 animate-ping"></span>
                <span class="relative inline-flex w-2 h-2 rounded-full bg-green-500"></span>
              </span>
              Live scan activity
            </div>
            <div class="text-[11px] mt-0.5 text-muted-foreground">
              SignalR — real-time
            </div>
          </div>
          <button hlmBtn variant="ghost" size="sm"
                  class="h-7 w-7 p-0"
                  (click)="toggleScanningActivity()"
                  aria-label="Close activity feed">
            <ng-icon hlm name="lucideX" class="size-4" />
          </button>
        </div>

        <div class="max-h-[520px] overflow-y-auto">
          @for (activity of recentScanningActivity; track activity.scanTime) {
            <div class="px-4 py-3 border-b border-border last:border-0 flex gap-3 text-xs hover:bg-surface-raised">
              <span class="grid place-items-center w-7 h-7 rounded-full mt-0.5 ring-1 shrink-0"
                    [class.bg-green-50]="activity.success"
                    [class.text-green-700]="activity.success"
                    [class.ring-green-200]="activity.success"
                    [class.bg-red-50]="!activity.success"
                    [class.text-red-700]="!activity.success"
                    [class.ring-red-200]="!activity.success">
                <ng-icon hlm name="lucideScanLine" class="size-3.5" />
              </span>
              <div class="flex-1 min-w-0">
                <div>
                  <span class="font-medium">{{ activity.scannerUserName }}</span>
                  scanned
                  <span class="font-mono">{{ activity.barcode }}</span>
                </div>
                <div class="mt-0.5 flex items-center gap-1.5 text-muted-foreground">
                  @if (activity.orderId) {
                    <span class="font-mono">{{ activity.orderId }}</span>
                    <span>·</span>
                  }
                  <span>{{ activity.scanTime | date:'HH:mm:ss' }}</span>
                </div>
              </div>
            </div>
          }
        </div>

        <div class="px-4 py-3 border-t border-border text-[11px] flex items-center justify-between text-muted-foreground">
          <span>Last update: {{ getTimeSinceLastUpdate() }}</span>
          <button hlmBtn variant="ghost" size="sm"
                  class="h-6 text-xs"
                  (click)="clearRealtimeData()">
            Clear
          </button>
        </div>
      </div>
    }

  </div><!-- /grid -->
</div>
